import { useQuery } from '@tanstack/react-query';
import { FormField, Input, Select } from '../../../components/ui/FormField.js';
import { Switch } from '../../../components/ui/Switch.js';
import { api } from '../../../services/api.js';
import { BUFFER_CHOICES, bufferLabel } from '../../../utils/bufferMinutes.js';
import { LastChanged } from '../parts/LastChanged.js';
import { PendingChanges, SettingCard, StatusChip } from '../parts/parts.js';
import { type ImpactInspectionWindows, impactPath } from '../settingsApi.js';
import { PRECEDENCE_LABELS } from '../settingsFormat.js';
import type { InspectionWindows, OperatingHoursPrecedence } from '../settingsTypes.js';
import type { CardProps } from './cardProps.js';

/** 039: how a car's and its location's operating hours combine. */
export function OperatingHoursCard({ draft, latest, refusal }: CardProps) {
  const saved = draft.saved?.operating_hours_precedence;
  const value = draft.value('operating_hours_precedence');
  if (!saved || !value) return null;
  return (
    <SettingCard
      testId="operating-hours-card"
      title="Operating hours"
      dirty={draft.isDirty('operating_hours_precedence')}
      refusal={refusal('operating_hours_precedence')}
      chip={
        <StatusChip testId="status-operating_hours" tone="off">
          {PRECEDENCE_LABELS[saved]}
        </StatusChip>
      }
      description={
        <p>
          Controls how a car's and its pickup location's operating hours combine when a booking is
          evaluated. Takes effect immediately for new bookings.
        </p>
      }
      footer={<LastChanged setting="operating_hours" latest={latest} />}
    >
      <FormField label="Operating hours precedence" name="operating_hours_precedence">
        <Select
          id="operating_hours_precedence"
          name="operating_hours_precedence"
          value={value}
          onChange={(e) =>
            draft.set('operating_hours_precedence', e.target.value as OperatingHoursPrecedence)
          }
        >
          {Object.entries(PRECEDENCE_LABELS).map(([v, label]) => (
            <option key={v} value={v}>
              {label}
            </option>
          ))}
        </Select>
      </FormField>
      <PendingChanges
        setting="operating_hours"
        changes={draft.changesOf('operating_hours_precedence')}
      />
    </SettingCard>
  );
}

/** 131: the two extension policy guards. */
export function ExtensionsCard({ draft, latest, refusal }: CardProps) {
  const saved = draft.saved?.extension_policy;
  const policy = draft.value('extension_policy');
  if (!saved || !policy) return null;
  const max = saved.max_total_extension_minutes;
  return (
    <SettingCard
      testId="extensions-card"
      title="Rental extensions"
      dirty={draft.isDirty('extension_policy')}
      refusal={refusal('extension_policy')}
      chip={
        <StatusChip testId="status-extension_policy" tone="off">
          {`${saved.min_notice_minutes} min notice · ${max === null ? 'no maximum' : `max ${max} min`}`}
        </StatusChip>
      }
      description={
        <p>
          How close to the return time an extension may still be started, and the most total time
          one rental may be extended by. Applies to the next extension request; an extension already
          awaiting payment is unaffected.
        </p>
      }
      footer={<LastChanged setting="extension_policy" latest={latest} />}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label="Minimum notice (minutes)" name="extension_min_notice">
          <Input
            id="extension_min_notice"
            type="number"
            min={0}
            max={10080}
            step="1"
            value={policy.min_notice_minutes}
            onChange={(e) =>
              draft.set('extension_policy', {
                ...policy,
                min_notice_minutes: Number(e.target.value),
              })
            }
          />
        </FormField>
        <FormField label="Maximum total extension (minutes)" name="extension_max_total">
          <Input
            id="extension_max_total"
            type="number"
            min={30}
            max={525600}
            step="1"
            disabled={policy.max_total_extension_minutes === null}
            value={policy.max_total_extension_minutes ?? ''}
            onChange={(e) =>
              draft.set('extension_policy', {
                ...policy,
                max_total_extension_minutes: Number(e.target.value),
              })
            }
          />
        </FormField>
      </div>
      <label className="flex items-center gap-2 text-sm text-ink">
        <input
          type="checkbox"
          checked={policy.max_total_extension_minutes === null}
          onChange={(e) =>
            draft.set('extension_policy', {
              ...policy,
              max_total_extension_minutes: e.target.checked ? null : 30,
            })
          }
        />
        No maximum
      </label>
      <PendingChanges setting="extension_policy" changes={draft.changesOf('extension_policy')} />
    </SettingCard>
  );
}

/** 386 (#1278, FR-005): the gap kept between two customers' rentals of the same car. */
export function BookingBufferCard({ draft, latest, refusal }: CardProps) {
  const saved = draft.saved?.booking_buffer_minutes;
  const value = draft.value('booking_buffer_minutes');
  if (saved == null || value == null) return null;
  return (
    <SettingCard
      testId="booking-buffer-card"
      title="Gap between rentals"
      dirty={draft.isDirty('booking_buffer_minutes')}
      refusal={refusal('booking_buffer_minutes')}
      chip={
        <StatusChip testId="status-booking_buffer" tone="off">
          {bufferLabel(saved)}
        </StatusChip>
      }
      description={
        <p>
          Customers cannot book a car closer than this to another customer's rental. Staff may still
          go closer; they are warned. A car can have its own gap on its page.
        </p>
      }
      footer={<LastChanged setting="booking_buffer" latest={latest} />}
    >
      <FormField label="Gap" name="booking_buffer_minutes">
        <Select
          id="booking_buffer_minutes"
          name="booking_buffer_minutes"
          value={String(value)}
          onChange={(e) => draft.set('booking_buffer_minutes', Number(e.target.value))}
        >
          {BUFFER_CHOICES.map((c) => (
            <option key={c.value} value={String(c.value)}>
              {c.label}
            </option>
          ))}
        </Select>
      </FormField>
      <p className="text-xs text-steel">
        Default 30 minutes. Rentals already closer than this are left as they are.
      </p>
      <p className="rounded-md bg-surface-feature px-3 py-2 text-sm text-ink">
        Example with 30 minutes: a rental ends 16:30, so the next customer can pick up from 17:00.
        One gap is shared between two rentals, never added twice.
      </p>
      <PendingChanges
        setting="booking_buffer"
        changes={draft.changesOf('booking_buffer_minutes')}
      />
    </SettingCard>
  );
}

/** 374: the defaults the platform fills for a settings document that predates the windows. */
const WINDOW_DEFAULTS: InspectionWindows = {
  pickup_before_minutes: 15,
  pickup_after_minutes: 120,
  return_before_minutes: 30,
  return_after_minutes: 120,
};

/** "1 h", "2 h 30 min", "45 min". */
export function minutesLabel(m: number): string {
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${h} h ${rest} min` : `${h} h`;
}

/** "09:45" for an example moment at 10:00 shifted by `delta` minutes (whole-day wrap is fine for an example). */
function clock(delta: number): string {
  const total = (((10 * 60 + delta) % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

function MomentWindow({
  moment,
  before,
  after,
  savedBefore,
  savedAfter,
  onBefore,
  onAfter,
}: {
  moment: 'pickup' | 'return';
  before: number;
  after: number;
  savedBefore: number;
  savedAfter: number;
  onBefore: (n: number) => void;
  onAfter: (n: number) => void;
}) {
  const title = moment === 'pickup' ? 'At pickup' : 'At return';
  const word = moment === 'pickup' ? 'pickup' : 'return';
  // The bar: a 14-hour span from 2 h before the moment, so every allowed value fits on it.
  const span = 2 * 60 + 12 * 60;
  const pct = (m: number) => `${Math.min(100, Math.max(0, ((m + 120) / span) * 100))}%`;
  const valid =
    Number.isInteger(before) &&
    Number.isInteger(after) &&
    before >= 0 &&
    before <= 120 &&
    after >= 15 &&
    after <= 720;
  const field = (
    id: string,
    label: string,
    value: number,
    saved: number,
    min: number,
    max: number,
    onChange: (n: number) => void,
  ) => (
    <FormField label={`${label} (minutes, ${min}–${max})`} name={id}>
      <Input
        id={id}
        type="number"
        min={min}
        max={max}
        step="1"
        value={Number.isNaN(value) ? '' : value}
        className={value !== saved ? 'border-primary bg-primary/5' : undefined}
        onChange={(e) => onChange(e.target.value === '' ? Number.NaN : Number(e.target.value))}
      />
    </FormField>
  );
  return (
    <div
      data-testid={`window-${moment}`}
      className="flex flex-col gap-3 rounded-lg border border-hairline p-3"
    >
      <span className="text-sm font-semibold text-ink">{title}</span>
      <div className="grid gap-3 sm:grid-cols-2">
        {field(
          `${word}_before_minutes`,
          `Opens before ${word}`,
          before,
          savedBefore,
          0,
          120,
          onBefore,
        )}
        {field(
          `${word}_after_minutes`,
          `Closes after ${word}`,
          after,
          savedAfter,
          15,
          720,
          onAfter,
        )}
      </div>
      {valid && (
        <>
          <div className="relative mx-1.5 mt-1 h-6" aria-hidden="true">
            <div className="absolute inset-x-0 top-3 h-1.5 rounded-full bg-surface-feature" />
            <div
              className="absolute top-3 h-1.5 rounded-full bg-brand-yellow"
              style={{ left: pct(-before), width: `calc(${pct(after)} - ${pct(-before)})` }}
            />
            <div className="absolute top-1 h-5 w-0.5 bg-ink" style={{ left: pct(0) }} />
          </div>
          <p
            data-testid={`window-example-${moment}`}
            className="mt-2 rounded-md bg-surface px-2.5 py-1.5 text-sm text-ink"
          >
            Opens {minutesLabel(before)} before, closes {minutesLabel(after)} after. A 10:00 {word}:
            photos from <b>{clock(-before)}</b> to <b>{clock(after)}</b>
            {moment === 'return' ? ', even after the booking closes on its own.' : '.'}
          </p>
        </>
      )}
    </div>
  );
}

/** 374: how many rentals have a window open right now — the ones a save moves. Asked only while the windows are edited. */
function OpenWindowsNotice({ ask }: { ask: boolean }) {
  const impact = useQuery({
    queryKey: ['platform-config', 'impact', 'inspection_windows'],
    queryFn: () => api.get<ImpactInspectionWindows>(impactPath('inspection_windows')),
    enabled: ask,
    retry: false,
    staleTime: 0,
  });
  if (!ask) return null;
  const d = impact.data;
  const open =
    d && d.pickup_open + d.return_open > 0
      ? `, including ${[
          d.pickup_open > 0
            ? `${d.pickup_open} ${d.pickup_open === 1 ? 'rental' : 'rentals'} whose pickup window`
            : null,
          d.return_open > 0
            ? `${d.return_open} ${d.return_open === 1 ? 'rental' : 'rentals'} whose return window`
            : null,
        ]
          .filter(Boolean)
          .join(
            ' and ',
          )} ${d.pickup_open + d.return_open === 1 ? 'is open right now (it closes' : 'are open right now (they close'} at the new time)`
      : '';
  return (
    <p
      data-testid="inspection-windows-impact"
      className="rounded-md bg-semantic-warning-bg px-3 py-2 text-sm text-semantic-warning-text"
    >
      Saving changes the photo windows for every booking from now on{open}.
    </p>
  );
}

/**
 * 337 + 356: customers' pickup and return photos, saved with the tab, not on click. 374 (#1238): with when customers
 * can take them — four minute fields, a bar and an example per moment. One card, two settings keys.
 */
export function InspectionPhotosCard({ draft, latest, refusal }: CardProps) {
  if (!draft.saved) return null;
  const savedOn = draft.saved.customer_inspection_photos;
  const on = draft.value('customer_inspection_photos') !== false;
  const savedWindows = draft.saved.inspection_windows ?? WINDOW_DEFAULTS;
  const windows = draft.value('inspection_windows') ?? savedWindows;
  const set = (patch: Partial<InspectionWindows>) =>
    draft.set('inspection_windows', { ...windows, ...patch });
  const windowsDirty = draft.isDirty('inspection_windows');
  return (
    <SettingCard
      testId="inspection-photos-card"
      title="Customer inspection photos"
      dirty={draft.isDirty('customer_inspection_photos') || windowsDirty}
      refusal={refusal('customer_inspection_photos') ?? refusal('inspection_windows')}
      chip={
        <StatusChip
          testId="status-inspection_photos"
          tone={savedOn ? 'on' : 'off'}
          since={latest?.inspection_photos?.state_since}
        >
          {savedOn ? 'ON' : 'OFF'}
        </StatusChip>
      }
      description={
        <p>
          {on
            ? 'Customers photograph the car at pickup and at return. Each set can be sent once, inside its window.'
            : 'Customers are not offered inspection photos. Staff inspections are unaffected.'}
        </p>
      }
      footer={<LastChanged setting="inspection_photos" latest={latest} />}
    >
      <div className="flex items-center gap-3 text-sm text-ink">
        <Switch
          checked={on}
          onChange={() => draft.set('customer_inspection_photos', !on)}
          ariaLabel="Customer inspection photos"
        />
        <span>{on ? 'On' : 'Off'}</span>
      </div>
      <div className="flex flex-col gap-3 border-t border-hairline pt-3">
        <h3 className="text-sm font-semibold text-ink">When customers can take the photos</h3>
        <div className="grid gap-3 lg:grid-cols-2">
          <MomentWindow
            moment="pickup"
            before={windows.pickup_before_minutes}
            after={windows.pickup_after_minutes}
            savedBefore={savedWindows.pickup_before_minutes}
            savedAfter={savedWindows.pickup_after_minutes}
            onBefore={(n) => set({ pickup_before_minutes: n })}
            onAfter={(n) => set({ pickup_after_minutes: n })}
          />
          <MomentWindow
            moment="return"
            before={windows.return_before_minutes}
            after={windows.return_after_minutes}
            savedBefore={savedWindows.return_before_minutes}
            savedAfter={savedWindows.return_after_minutes}
            onBefore={(n) => set({ return_before_minutes: n })}
            onAfter={(n) => set({ return_after_minutes: n })}
          />
        </div>
        <p className="text-xs text-steel">
          For an extended rental, pickup is the first leg's pickup and return is the last leg's
          return. Staff inspections have no window.
        </p>
      </div>
      <OpenWindowsNotice ask={windowsDirty} />
      <PendingChanges
        setting="inspection_photos"
        changes={[
          ...draft.changesOf('customer_inspection_photos'),
          ...draft.changesOf('inspection_windows'),
        ]}
      />
    </SettingCard>
  );
}
